// src/components/AIModelManager.tsx
import React, { useState, useEffect, FC } from 'react'
import type { ColumnsType } from 'antd/es/table'
import {
  Alert,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  Tag,
  message,
  Space,
  Card,
  Row,
  Col,
  Divider,
  Checkbox,
  Popconfirm
} from 'antd'
import { PlusOutlined, EditOutlined, MinusCircleOutlined, DeleteOutlined } from '@ant-design/icons'
import adminAxios from './adminAxios'
import { resolveFrontendApiBase } from '../utils/apiBase'
import './AIModelManager.css'

type Provider =
  | 'openai'
  | 'anthropic_openrouter'
  | 'gemini'
  | 'generic_openrouter'
  | 'deepseek_openrouter'
  | 'comet'

type Tier = 'Best' | 'Better' | 'Good' | 'None'

type LanguagePairTiers = Record<string, Record<string, Tier>>

interface LanguagePairOverride {
  source: string
  target: string
  tier: Tier
}

type AIModelFormValues = AIModel & {
  languagePairOverrides?: LanguagePairOverride[]
  imageCostBySizeJson?: string
}

export interface AIModel {
  id: string
  displayName: string
  apiModel: string
  provider: Provider
  displayOrder?: number
  defaultForWorkflows?: string[]
  launchReady?: boolean
  translationReasoningEffort?: string
  deprecationDate?: string
  defaultTier: Tier
  languageTiers?: Record<string, Tier>
  languagePairTiers?: LanguagePairTiers
  // New: pricing per 1M tokens
  inputCostPerMTokens?: number
  cachedInputCostPerMTokens?: number
  cacheWriteCostPerMTokens?: number
  outputCostPerMTokens?: number
  longContextThresholdTokens?: number
  longContextInputCostPerMTokens?: number
  longContextCachedInputCostPerMTokens?: number
  longContextCacheWriteCostPerMTokens?: number
  longContextOutputCostPerMTokens?: number
  ttsCostPerMCharacters?: number
  imageCostPerImage?: number
  imageCostBySize?: Record<string, number>
  providerPricingNotes?: string
  // New: disable model for specific languages
  disabledLanguages?: string[]
  isActive: boolean
  isFree: boolean
  allowsNSFW: boolean
  testingOnly: boolean
  internalOnly: boolean
  discount: number
  premium: number
  tags: string[]
  // New: optional hard cap for chunking
  maxCharsPerChunk?: number
  chunkSizeConfig?: {
    normalContent?: {
      smallThreshold: number
      chunkRange: [number, number]
    }
    rejectedContent?: Record<string, [number, number]>
  }
  updatedAt?: string
}

interface TierRecommendationLockInfo {
  locked: boolean
  reason?: string | null
}

interface TierRecommendationItem {
  model_id: string
  display_name: string
  current_tier: Tier
  proposed_tier?: Tier | null
  overall_score?: number | null
  score_count: number
  test_count: number
  distinct_text_count: number
  distinct_evaluator_count: number
  distinct_batch_count: number
  eligible_for_proposal: boolean
  insufficient_data: boolean
  manual_lock: TierRecommendationLockInfo
  outlier: 'none' | 'high' | 'low'
}

interface TierRecommendationResponse {
  recommendation_id: string
  language: string
  summary: {
    rows_scanned: number
    rows_after_proofreader_filter: number
    eligible_models_total: number
    models_meeting_min_tests: number
    models_with_proposals: number
    min_tests_required: number
  }
  items: TierRecommendationItem[]
}

const API_ORIGIN = resolveFrontendApiBase()
const API_BASE = `${API_ORIGIN}/api/ai-models`
const API_ROOT = `${API_ORIGIN}/api`
const RESET_CACHE_ENDPOINT = `${API_ORIGIN}/api/reset-ai-model-cache`
const TIER_RECOMMENDATIONS_ENDPOINT = `${API_ROOT}/ai-testing-tier-recommendations`
const TIER_RECOMMENDATIONS_APPLY_ENDPOINT = `${API_ROOT}/ai-testing-tier-recommendations/apply`
const TIER_RECOMMENDATIONS_LOCK_ENDPOINT = `${API_ROOT}/ai-testing-tier-recommendations/lock`

const SUPPORTED_LANGUAGES = [
  'German',
  'European Spanish',
  'Latin American Spanish',
  'Dutch',
  'Italian',
  'Hindi',
  'Brazilian Portuguese',
  'European Portuguese',
  'French',
  'Japanese',
  'Korean',
  'Traditional Chinese',
  'Simplified Chinese',
  'English',
  'Russian',
  'British English',
  'Thai',
  'Turkish',
  'Catalan',
  'Ukrainian',
  'Polish',
  'Swedish',
  'Hebrew',
  'Serbian',
  'Norwegian',
  'Afrikaans',  
  'Czech', 'Danish', 'Bosnian', 'Croatian', 'Finnish', 'Icelandic', 'Bulgarian',
]

const allowedPairTiers: Tier[] = ['Best', 'Better', 'Good', 'None']

const convertPairMapToOverrides = (pairTiers?: LanguagePairTiers): LanguagePairOverride[] => {
  if (!pairTiers) return []
  const overrides: LanguagePairOverride[] = []

  Object.entries(pairTiers).forEach(([target, sources]) => {
    if (!sources) return
    Object.entries(sources).forEach(([source, tier]) => {
      if (!tier || !allowedPairTiers.includes(tier as Tier) || tier === 'None') return
      overrides.push({ source, target, tier: tier as Tier })
    })
  })

  return overrides.sort((a, b) => {
    const targetDiff = a.target.localeCompare(b.target)
    if (targetDiff !== 0) return targetDiff
    const sourceDiff = a.source.localeCompare(b.source)
    if (sourceDiff !== 0) return sourceDiff
    return allowedPairTiers.indexOf(a.tier) - allowedPairTiers.indexOf(b.tier)
  })
}

const convertOverridesToPairMap = (overrides?: LanguagePairOverride[]): LanguagePairTiers => {
  const result: LanguagePairTiers = {}
  if (!overrides) return result

  overrides.forEach(({ source, target, tier }) => {
    const trimmedSource = (source || '').trim()
    const trimmedTarget = (target || '').trim()
    if (!trimmedSource || !trimmedTarget) return
    if (!tier || !allowedPairTiers.includes(tier)) return
    if (tier === 'None') return

    if (!result[trimmedTarget]) {
      result[trimmedTarget] = {}
    }
    result[trimmedTarget]![trimmedSource] = tier
  })

  return result
}

const AIModelManager: FC = () => {
  const [models, setModels] = useState<AIModel[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false)
  const [editingModel, setEditingModel] = useState<AIModel | null>(null)
  const [showInactive, setShowInactive] = useState<boolean>(true)
  const [deletingModelId, setDeletingModelId] = useState<string | null>(null)
  const [recommendationLanguage, setRecommendationLanguage] = useState<string>('Italian')
  const [recommendationDateFrom, setRecommendationDateFrom] = useState<string>('')
  const [recommendationDateTo, setRecommendationDateTo] = useState<string>('')
  const [includeTestingModels, setIncludeTestingModels] = useState<boolean>(false)
  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(false)
  const [recommendationApplying, setRecommendationApplying] = useState<boolean>(false)
  const [recommendationResult, setRecommendationResult] = useState<TierRecommendationResponse | null>(null)
  const [selectedRecommendationModelIds, setSelectedRecommendationModelIds] = useState<React.Key[]>([])
  const [lockUpdatingModelId, setLockUpdatingModelId] = useState<string | null>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [form] = Form.useForm<AIModelFormValues>()
  const [addForm] = Form.useForm<AIModelFormValues>()

useEffect(() => {
  fetchModels(true, showInactive) // Always bypass cache in admin UI
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [])

const fetchModels = async (
  bypassCache: boolean = true,
  includeInactive?: boolean
): Promise<void> => {
  setLoading(true)
  setInlineError(null)
  try {
    const shouldIncludeInactive = includeInactive ?? showInactive
    const params = new URLSearchParams({ bypass_cache: String(bypassCache) })
    if (shouldIncludeInactive) {
      params.set('includeInactive', 'true')
    }
    params.set('includeInternal', 'true')
    // Add bypass_cache parameter to always get fresh data in the admin UI
    const { data } = await adminAxios.get<AIModel[]>(
      `${API_BASE}?${params.toString()}`
    )
    console.log('[AIModelManager] fetchModels', { bypassCache, count: data.length, sample: data[0] })
    setModels(
      data.map(m => ({
        ...m,
        discount: Number(m.discount || 0),
        premium: Number(m.premium || 0),
        updatedAt: m.updatedAt
          ? new Date(m.updatedAt).toLocaleString()
          : undefined
      }))
    )
  } catch (err) {
    console.error(err)
    setInlineError('Failed to load AI models')
    message.error('Failed to load AI models')
  } finally {
    setLoading(false)
  }
}

const handleShowInactiveChange = (checked: boolean) => {
  setShowInactive(checked)
  fetchModels(false, checked)
}

const prepareModelPayload = (values: AIModelFormValues): AIModel => {
  const {
    languagePairOverrides,
    imageCostBySizeJson,
    updatedAt,
    languagePairTiers: _existingPairTiers,
    ...rest
  } = values

  const languagePairTiers = convertOverridesToPairMap(languagePairOverrides)
  const trimmedDeprecationDate = (rest.deprecationDate || '').trim()
  let imageCostBySize = rest.imageCostBySize
  if (typeof imageCostBySizeJson === 'string' && imageCostBySizeJson.trim()) {
    try {
      const parsed = JSON.parse(imageCostBySizeJson)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        imageCostBySize = Object.fromEntries(
          Object.entries(parsed).map(([key, value]) => [key, Number(value)])
        )
      }
    } catch {
      imageCostBySize = rest.imageCostBySize
    }
  }
  console.log('[AIModelManager] prepareModelPayload', { id: rest.id, languagePairTiers })

  const payload: AIModel = {
    ...rest,
    imageCostBySize,
    discount: Number(rest.discount || 0),
    premium: Number(rest.premium || 0),
    ...(trimmedDeprecationDate ? { deprecationDate: trimmedDeprecationDate } : {}),
    languagePairTiers,
  }

  return payload
}

const handleEdit = (model: AIModel): void => {
  setEditingModel(model)
  setEditModalVisible(true)
}

useEffect(() => {
  if (editModalVisible && editingModel) {
    // Reset form first to clear any previous values
    form.resetFields()
    
    // Initialize languageTiers with all languages set to 'None' first
    const languageTiers = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
      acc[lang] = 'None'
      return acc
    }, {} as Record<string, Tier>)
    
    // Then override with actual values from the model
    if (editingModel.languageTiers) {
      Object.assign(languageTiers, editingModel.languageTiers)
    }
    
    // Set all form values including the properly initialized languageTiers
    form.setFieldsValue({
      ...editingModel,
      languageTiers,
      premium: editingModel.premium ?? 0,
      inputCostPerMTokens: editingModel.inputCostPerMTokens ?? 0,
      cachedInputCostPerMTokens: editingModel.cachedInputCostPerMTokens,
      cacheWriteCostPerMTokens: editingModel.cacheWriteCostPerMTokens,
      outputCostPerMTokens: editingModel.outputCostPerMTokens ?? 0,
      ttsCostPerMCharacters: editingModel.ttsCostPerMCharacters,
      imageCostPerImage: editingModel.imageCostPerImage,
      imageCostBySize: editingModel.imageCostBySize,
      imageCostBySizeJson: editingModel.imageCostBySize ? JSON.stringify(editingModel.imageCostBySize, null, 2) : '',
      providerPricingNotes: editingModel.providerPricingNotes,
      maxCharsPerChunk: editingModel.maxCharsPerChunk,
      disabledLanguages: editingModel.disabledLanguages ?? [],
      languagePairOverrides: convertPairMapToOverrides(editingModel.languagePairTiers)
    })
    console.log('[AIModelManager] Loaded model into form', {
      id: editingModel.id,
      languagePairTiers: editingModel.languagePairTiers
    })
  }
}, [editModalVisible, editingModel, form])

const handleModalClose = (): void => {
  setEditModalVisible(false)
  setEditingModel(null)
  form.resetFields()
}

  const handleAdd = (): void => {
    addForm.resetFields()
    // Set default language tiers
    const defaultLanguageTiers = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
      acc[lang] = 'None'
      return acc
    }, {} as Record<string, Tier>)
    
    addForm.setFieldsValue({
      isActive: true,
      isFree: false,
      allowsNSFW: false,
      testingOnly: false,
      internalOnly: false,
      discount: 0,
      premium: 0,
      displayOrder: undefined,
      defaultForWorkflows: [],
      launchReady: false,
      translationReasoningEffort: undefined,
      tags: [],
      defaultTier: 'None',
      languageTiers: defaultLanguageTiers,
      languagePairOverrides: [],
      inputCostPerMTokens: 0,
      cachedInputCostPerMTokens: undefined,
      cacheWriteCostPerMTokens: undefined,
      outputCostPerMTokens: 0,
      ttsCostPerMCharacters: undefined,
      imageCostPerImage: undefined,
      imageCostBySize: undefined,
      imageCostBySizeJson: '',
      providerPricingNotes: '',
      maxCharsPerChunk: undefined,
      chunkSizeConfig: {
        normalContent: {
          smallThreshold: 8000,
          chunkRange: [7000, 8000]
        },
        rejectedContent: {}
      },
      disabledLanguages: []
    })
    setAddModalVisible(true)
  }

const handleSave = async (payload: AIModel): Promise<void> => {
  setInlineError(null)
  try {
    console.log('[AIModelManager] handleSave', payload)
    await adminAxios.put(`${API_BASE}/${payload.id}`, payload)

    // Optional: Explicitly reset cache after update
    await adminAxios.post(RESET_CACHE_ENDPOINT)

    message.success('Model updated successfully')
    setEditModalVisible(false)

    // Fetch with cache bypass to ensure fresh data
    fetchModels(true, showInactive)
  } catch (error) {
    console.error('[AIModelManager] Error saving model', error)
    setInlineError('Error saving model')
    message.error('Error saving model')
  }
}

const handleCreate = async (payload: AIModel): Promise<void> => {
  setInlineError(null)
  try {
    console.log('[AIModelManager] handleCreate', payload)
    await adminAxios.post(API_BASE, payload)

    // Optional: Explicitly reset cache after creation
    await adminAxios.post(RESET_CACHE_ENDPOINT)

    message.success('Model created successfully')
    setAddModalVisible(false)

    // Fetch with cache bypass to ensure fresh data
    fetchModels(true, showInactive)
  } catch (error) {
    console.error('[AIModelManager] Error creating model', error)
    setInlineError('Error creating model')
    message.error('Error creating model')
  }
}

const handleDelete = async (modelId: string): Promise<void> => {
  setDeletingModelId(modelId)
  setInlineError(null)
  try {
    console.log('[AIModelManager] handleDelete', modelId)
    await adminAxios.delete(`${API_BASE}/${encodeURIComponent(modelId)}`)

    // Optional: Explicitly reset cache after deletion
    await adminAxios.post(RESET_CACHE_ENDPOINT)

    message.success(`Model '${modelId}' deleted successfully`)

    if (editingModel?.id === modelId) {
      handleModalClose()
    }

    // Fetch with cache bypass to ensure fresh data
    await fetchModels(true, showInactive)
  } catch (error) {
    console.error('[AIModelManager] Error deleting model', error)
    setInlineError('Error deleting model')
    message.error('Error deleting model')
  } finally {
    setDeletingModelId(null)
  }
}

const fetchTierRecommendations = async (): Promise<void> => {
  if (!recommendationLanguage) {
    message.warning('Select a language first')
    return
  }

  setRecommendationLoading(true)
  try {
    const params: Record<string, string | boolean> = {
      language: recommendationLanguage,
      saveSnapshot: true,
      includeTestingModels,
    }
    if (recommendationDateFrom) params.fromDate = recommendationDateFrom
    if (recommendationDateTo) params.toDate = recommendationDateTo

    const { data } = await adminAxios.get<TierRecommendationResponse>(
      TIER_RECOMMENDATIONS_ENDPOINT,
      { params }
    )

    setRecommendationResult(data)
    const defaultSelected = (data.items || [])
      .filter(item => !!item.proposed_tier && !item.manual_lock?.locked)
      .map(item => item.model_id)
    setSelectedRecommendationModelIds(defaultSelected)
  } catch (err) {
    console.error('[AIModelManager] Failed to fetch tier recommendations', err)
    message.error('Failed to load AI testing tier recommendations')
  } finally {
    setRecommendationLoading(false)
  }
}

const handleApplyRecommendations = async (): Promise<void> => {
  if (!recommendationResult) {
    message.warning('Generate recommendations first')
    return
  }

  const selectedSet = new Set(selectedRecommendationModelIds.map(String))
  const updates = recommendationResult.items
    .filter(item => selectedSet.has(item.model_id))
    .filter(item => !!item.proposed_tier)
    .filter(item => !item.manual_lock?.locked)
    .map(item => ({
      modelId: item.model_id,
      tier: item.proposed_tier as Tier,
    }))

  if (!updates.length) {
    message.warning('No eligible recommended rows selected to apply')
    return
  }

  setRecommendationApplying(true)
  try {
    await adminAxios.post(TIER_RECOMMENDATIONS_APPLY_ENDPOINT, {
      language: recommendationResult.language,
      recommendationId: recommendationResult.recommendation_id,
      confirm: true,
      updates,
    })

    await adminAxios.post(RESET_CACHE_ENDPOINT)
    await fetchModels(true, showInactive)
    await fetchTierRecommendations()
    message.success(`Applied ${updates.length} language tier recommendation(s)`)
  } catch (err) {
    console.error('[AIModelManager] Failed to apply tier recommendations', err)
    message.error('Failed to apply selected recommendations')
  } finally {
    setRecommendationApplying(false)
  }
}

const handleToggleRecommendationLock = async (item: TierRecommendationItem): Promise<void> => {
  if (!recommendationResult) return

  const currentlyLocked = !!item.manual_lock?.locked
  let reason: string | undefined
  if (!currentlyLocked) {
    const prompted = window.prompt(
      `Optional lock reason for ${item.display_name} (${item.model_id}) in ${recommendationResult.language}:`,
      item.manual_lock?.reason || ''
    )
    reason = prompted != null ? prompted : undefined
  }

  setLockUpdatingModelId(item.model_id)
  try {
    await adminAxios.post(TIER_RECOMMENDATIONS_LOCK_ENDPOINT, {
      modelId: item.model_id,
      language: recommendationResult.language,
      locked: !currentlyLocked,
      reason,
    })
    await fetchTierRecommendations()
    message.success(
      `${item.model_id} ${currentlyLocked ? 'unlocked' : 'locked'} for ${recommendationResult.language}`
    )
  } catch (err) {
    console.error('[AIModelManager] Failed to toggle recommendation lock', err)
    message.error('Failed to update manual lock')
  } finally {
    setLockUpdatingModelId(null)
  }
}

  const getTierColor = (tier: Tier): string => {
    switch (tier) {
      case 'Best': return 'gold'
      case 'Better': return 'blue'
      case 'Good': return 'green'
      default: return 'default'
    }
  }

  const renderLanguageTiers = (languageTiers?: Record<string, Tier>): JSX.Element => {
    if (!languageTiers) return <span>—</span>
    
    const activeTiers = Object.entries(languageTiers).filter(([_, tier]) => tier !== 'None')
    
    if (activeTiers.length === 0) return <span>—</span>
    
    return (
      <Space size={[0, 4]} wrap>
        {activeTiers.map(([lang, tier]) => (
          <Tag
            key={lang}
            color={getTierColor(tier)}
            style={{ margin: '2px' }}
          >
            {lang}: {tier}
          </Tag>
        ))}
      </Space>
    )
  }

  const renderLanguagePairTiers = (languagePairTiers?: LanguagePairTiers): JSX.Element => {
    const overrides = convertPairMapToOverrides(languagePairTiers)
    if (overrides.length === 0) return <span>—</span>

    return (
      <Space size={[0, 4]} wrap>
        {overrides.map(({ source, target, tier }) => (
          <Tag
            key={`${source}->${target}`}
            color={getTierColor(tier)}
            style={{ margin: '2px' }}
          >
            {source} → {target}: {tier}
          </Tag>
        ))}
      </Space>
    )
  }

  // Filter models based on showInactive setting
  const filteredModels = models.filter(model => showInactive || model.isActive)

  const columns: ColumnsType<AIModel> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 120 },
    { 
      title: 'Display Name', 
      dataIndex: 'displayName', 
      key: 'displayName', 
      width: 150,
      sorter: (a, b) => a.displayName.localeCompare(b.displayName),
      sortDirections: ['ascend', 'descend']
    },
    { title: 'API Model', dataIndex: 'apiModel', key: 'apiModel', width: 150 },
    {
      title: 'Display Order',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 120,
      render: (value?: number) => value ?? 'Legacy fallback',
      sorter: (a, b) => (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER),
    },
    {
      title: 'Deprecation Date',
      dataIndex: 'deprecationDate',
      key: 'deprecationDate',
      width: 140,
      render: (value?: string) => value || '—',
      sorter: (a, b) => (a.deprecationDate || '').localeCompare(b.deprecationDate || ''),
      sortDirections: ['ascend', 'descend']
    },
    { 
      title: 'Provider', 
      dataIndex: 'provider', 
      key: 'provider', 
      width: 120,
      sorter: (a, b) => a.provider.localeCompare(b.provider),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Default Tier',
      dataIndex: 'defaultTier',
      key: 'defaultTier',
      width: 100,
      render: (tier: Tier) => (
        <Tag color={getTierColor(tier)}>
          {tier}
        </Tag>
      ),
      sorter: (a, b) => {
        const tierOrder: Record<Tier, number> = { 'Best': 0, 'Better': 1, 'Good': 2, 'None': 3 }
        return tierOrder[a.defaultTier] - tierOrder[b.defaultTier]
      },
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Language Tiers',
      dataIndex: 'languageTiers',
      key: 'languageTiers',
      width: 250,
      render: renderLanguageTiers
    },
    {
      title: 'Source → Target Overrides',
      dataIndex: 'languagePairTiers',
      key: 'languagePairTiers',
      width: 320,
      render: renderLanguagePairTiers
    },
    {
      title: 'Pricing ($/M)',
      key: 'pricing',
      width: 180,
      render: (_, record: AIModel) => (
        <Space size={[4, 4]} wrap>
          <Tag color='blue'>In: {Number(record.inputCostPerMTokens ?? 0).toFixed(4)}</Tag>
          {record.cachedInputCostPerMTokens != null && (
            <Tag color='cyan'>Cached: {Number(record.cachedInputCostPerMTokens).toFixed(4)}</Tag>
          )}
          {record.cacheWriteCostPerMTokens != null && (
            <Tag color='gold'>Cache write: {Number(record.cacheWriteCostPerMTokens).toFixed(4)}</Tag>
          )}
          <Tag color='geekblue'>Out: {Number(record.outputCostPerMTokens ?? 0).toFixed(4)}</Tag>
        </Space>
      )
    },
    {
      title: 'Max Chars/Chunk',
      dataIndex: 'maxCharsPerChunk',
      key: 'maxCharsPerChunk',
      width: 140,
      render: (v?: number) => (v ? v.toLocaleString() : '—'),
      sorter: (a, b) => (a.maxCharsPerChunk || 0) - (b.maxCharsPerChunk || 0),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Disabled In',
      dataIndex: 'disabledLanguages',
      key: 'disabledLanguages',
      width: 220,
      render: (langs?: string[]) =>
        langs && langs.length > 0 ? (
          <Space size={[0, 4]} wrap>
            {langs.map(l => (
              <Tag key={l}>{l}</Tag>
            ))}
          </Space>
        ) : '—'
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record: AIModel) => (
        <Space direction="vertical" size="small">
          <Tag color={record.isActive ? 'green' : 'red'}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
          {record.isFree && <Tag color='cyan'>Free</Tag>}
          {record.testingOnly && <Tag color='orange'>Testing</Tag>}
          {record.internalOnly && <Tag color='purple'>Internal</Tag>}
          {record.allowsNSFW && <Tag color='red'>NSFW</Tag>}
        </Space>
      ),
      sorter: (a, b) => {
        // Sort by isActive first (active models first)
        if (a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1
        }
        // Secondary sort by other boolean properties
        if (a.isFree !== b.isFree) {
          return a.isFree ? -1 : 1
        }
        if (a.testingOnly !== b.testingOnly) {
          return a.testingOnly ? 1 : -1
        }
        return 0
      },
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 80,
      render: (discount: number) =>
        discount > 0 ? <Tag color='green'>{discount}% Off</Tag> : '—'
    },
    {
      title: 'Premium',
      dataIndex: 'premium',
      key: 'premium',
      width: 90,
      render: (premium: number) =>
        premium > 0 ? <Tag color='volcano'>+{premium}%</Tag> : '—'
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => (
        <Space size={[0, 4]} wrap>
          {tags.map(tag => (
            <Tag key={tag} color='purple'>{tag}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 190,
      render: (_, record: AIModel) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            type="primary"
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title={`Delete model "${record.id}"?`}
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              loading={deletingModelId === record.id}
              disabled={Boolean(deletingModelId && deletingModelId !== record.id)}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  const recommendationItems: TierRecommendationItem[] = recommendationResult?.items || []

  const recommendationColumns: ColumnsType<TierRecommendationItem> = [
    {
      title: 'Model',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 180,
      render: (_, record) => (
        <Space size="small" wrap>
          <span>{record.display_name}</span>
          <Tag>{record.model_id}</Tag>
        </Space>
      ),
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Current Tier',
      dataIndex: 'current_tier',
      key: 'current_tier',
      width: 120,
      render: (tier: Tier) => <Tag color={getTierColor(tier)}>{tier}</Tag>
    },
    {
      title: 'Proposed Tier',
      dataIndex: 'proposed_tier',
      key: 'proposed_tier',
      width: 140,
      render: (tier?: Tier | null) =>
        tier ? <Tag color={getTierColor(tier)}>{tier}</Tag> : <span>—</span>
    },
    {
      title: 'Score',
      dataIndex: 'overall_score',
      key: 'overall_score',
      width: 100,
      render: (value?: number | null) => (value != null ? Number(value).toFixed(3) : '—'),
      sorter: (a, b) => Number(a.overall_score || 0) - Number(b.overall_score || 0),
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Tests',
      dataIndex: 'test_count',
      key: 'test_count',
      width: 90,
      sorter: (a, b) => a.test_count - b.test_count,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Signals',
      key: 'signals',
      width: 260,
      render: (_, record) => (
        <Space size={[0, 4]} wrap>
          <Tag>{record.distinct_text_count} texts</Tag>
          <Tag>{record.distinct_evaluator_count} evaluators</Tag>
          <Tag>{record.distinct_batch_count} batches</Tag>
          {record.outlier !== 'none' && (
            <Tag color={record.outlier === 'high' ? 'gold' : 'red'}>
              {record.outlier === 'high' ? 'High outlier' : 'Low outlier'}
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 320,
      render: (_, record) => (
        <Space size={[0, 4]} wrap>
          {!record.eligible_for_proposal && <Tag color="orange">Insufficient data</Tag>}
          {record.manual_lock?.locked && <Tag color="red">Manual lock</Tag>}
          {record.manual_lock?.locked && record.manual_lock.reason && (
            <Tag>{record.manual_lock.reason}</Tag>
          )}
          {!record.proposed_tier && <Tag>No proposal</Tag>}
          <Button
            size='small'
            onClick={() => handleToggleRecommendationLock(record)}
            loading={lockUpdatingModelId === record.model_id}
          >
            {record.manual_lock?.locked ? 'Unlock' : 'Lock'}
          </Button>
        </Space>
      )
    }
  ]

  const recommendationRowSelection = {
    selectedRowKeys: selectedRecommendationModelIds,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRecommendationModelIds(selectedRowKeys)
    },
    getCheckboxProps: (record: TierRecommendationItem) => ({
      disabled: !record.proposed_tier || !!record.manual_lock?.locked
    }),
  }

  const renderModelForm = (formInstance: any, isAdd: boolean = false) => (
    <Form
      className='ai-model-form'
      form={formInstance}
      layout='vertical'
      onFinish={async values => {
        const payload = prepareModelPayload(values)
        console.log('[AIModelManager] submit', { isAdd, payload })
        if (isAdd) {
          await handleCreate(payload)
        } else {
          await handleSave(payload)
        }
      }}
    >
      <div className='ai-model-form-block'>
        <div className='ai-model-form-block__heading'>
          <span>Identity</span>
          <p>Model identifiers, provider routing, and default placement.</p>
        </div>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='id'
              label='Model ID'
              rules={[{ required: true, message: 'Please enter model ID' }]}
            >
              <Input disabled={!isAdd} placeholder="e.g., ChatGPT4.2" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='displayName'
              label='Display Name'
              rules={[{ required: true, message: 'Please enter display name' }]}
            >
              <Input placeholder="e.g., ChatGPT 4.2" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='apiModel'
              label='API Model String'
              rules={[{ required: true, message: 'Please enter API model string' }]}
            >
              <Input placeholder="e.g., gpt-4.2-turbo" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='provider'
              label='Provider'
              rules={[{ required: true, message: 'Please select provider' }]}
            >
              <Select placeholder="Select provider">
                <Select.Option value='openai'>OpenAI</Select.Option>
                <Select.Option value='comet'>Comet</Select.Option>
                <Select.Option value='anthropic_openrouter'>Anthropic (OpenRouter)</Select.Option>
                <Select.Option value='gemini'>Gemini</Select.Option>
                <Select.Option value='generic_openrouter'>Generic (OpenRouter)</Select.Option>
                <Select.Option value='deepseek_openrouter'>DeepSeek (OpenRouter)</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='defaultTier'
              label='Default Tier'
              rules={[{ required: true, message: 'Please select default tier' }]}
            >
              <Select placeholder="Select default tier">
                <Select.Option value='Best'>Best</Select.Option>
                <Select.Option value='Better'>Better</Select.Option>
                <Select.Option value='Good'>Good</Select.Option>
                <Select.Option value='None'>None</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='deprecationDate'
              label='Deprecation Date'
              tooltip='Optional model sunset date shown to users in model selectors'
              rules={[
                {
                  validator: (_, value) => {
                    const text = (value || '').trim()
                    if (!text) return Promise.resolve()
                    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return Promise.resolve()
                    return Promise.reject(new Error('Use YYYY-MM-DD'))
                  }
                }
              ]}
            >
              <Input placeholder='YYYY-MM-DD' />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='displayOrder'
              label='Display Order'
              tooltip='Lower numbers appear first. Tier order remains a compatibility fallback until every active model has a value.'
            >
              <InputNumber min={0} step={10} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name='translationReasoningEffort'
              label='Translation Reasoning'
            >
              <Select allowClear placeholder='Provider default'>
                {['none', 'low', 'medium', 'high', 'xhigh', 'max'].map(effort => (
                  <Select.Option key={effort} value={effort}>{effort}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='defaultForWorkflows' label='Workflow Defaults'>
              <Select mode='multiple' allowClear placeholder='No workflow default'>
                {['translation', 'proofreading', 'metadata', 'blurb', 'keywords'].map(workflow => (
                  <Select.Option key={workflow} value={workflow}>{workflow}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name='discount' label='Discount %'>
              <InputNumber min={0} max={100} placeholder="0" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name='premium' label='Premium %'>
              <InputNumber min={0} max={200} placeholder="0" />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className='ai-model-form-block'>
        <Divider orientation="left">Pricing</Divider>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='inputCostPerMTokens'
              label='Input Cost ($ per 1M tokens)'
              tooltip='Cost for prompt (input) tokens'
            >
              <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder="e.g., 3.00" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='outputCostPerMTokens'
              label='Output Cost ($ per 1M tokens)'
              tooltip='Cost for completion (output) tokens'
            >
              <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder="e.g., 15.00" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='cachedInputCostPerMTokens'
              label='Cached Input Cost ($ per 1M tokens)'
              tooltip='If omitted, analytics assumes 10% of input cost'
            >
              <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder="e.g., 0.30" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='cacheWriteCostPerMTokens'
              label='Cache Write Cost ($ per 1M tokens)'
              tooltip='Provider charge for tokens written to the prompt cache'
            >
              <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder="e.g., 3.75" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='ttsCostPerMCharacters'
              label='TTS Cost ($ per 1M chars)'
              tooltip='Optional provider cost for text-to-speech models'
            >
              <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder="e.g., 15.00" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='imageCostPerImage'
              label='Image Cost ($ per image)'
              tooltip='Optional provider cost for image generation models'
            >
              <InputNumber min={0} step={0.0001} style={{ width: '100%' }} placeholder="e.g., 0.04" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='imageCostBySizeJson'
              label='Image Cost By Size JSON'
              tooltip='Optional map such as {"1024x1536": 0.06}'
            >
              <Input.TextArea rows={1} placeholder='{"1024x1536": 0.06}' />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name='providerPricingNotes'
              label='Pricing Notes'
              tooltip='Optional context for non-token provider pricing'
            >
              <Input.TextArea rows={1} placeholder="e.g., fallback image estimate until provider usage lands" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='maxCharsPerChunk'
              label='Max Characters Per Chunk'
              tooltip='Optional hard cap for chunking size used in translation and proofreading'
            >
              <InputNumber min={1000} step={500} style={{ width: '100%' }} placeholder="e.g., 16000" />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className='ai-model-form-block'>
        <Divider orientation="left">Chunking</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['chunkSizeConfig','normalContent','smallThreshold']}
              label='Small Threshold (chars)'
              tooltip='If chapter length <= this, the entire chapter is sent as a single chunk (for normal content).'
            >
              <InputNumber min={1000} step={500} style={{ width: '100%' }} placeholder="e.g., 8000" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['chunkSizeConfig','normalContent','chunkRange',0]}
              label='Chunk Range Min (chars)'
              tooltip='Lower bound for randomized max-chunk size used when splitting.'
            >
              <InputNumber min={1000} step={500} style={{ width: '100%' }} placeholder="e.g., 7000" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['chunkSizeConfig','normalContent','chunkRange',1]}
              label='Chunk Range Max (chars)'
              tooltip='Upper bound for randomized max-chunk size used when splitting.'
            >
              <InputNumber min={2000} step={500} style={{ width: '100%' }} placeholder="e.g., 8000" />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <Divider orientation="left">Language-Specific Quality Tiers</Divider>
      <Card className='ai-model-form-card' size="small" title="Set quality tier for each language">
        <Row gutter={[16, 16]}>
          {SUPPORTED_LANGUAGES.map(language => (
            <Col span={6} key={language}>
              <Form.Item
                name={['languageTiers', language]}
                label={language}
              >
                <Select placeholder="Select tier" allowClear>
                  <Select.Option value='Best'>Best</Select.Option>
                  <Select.Option value='Better'>Better</Select.Option>
                  <Select.Option value='Good'>Good</Select.Option>
                  <Select.Option value='None'>None</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Card>

      <Divider orientation="left">Source To Target Overrides</Divider>
      <Card className='ai-model-form-card' size="small" title="Prioritize models for specific language pairs">
        <Form.List name='languagePairOverrides'>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  className='ai-model-override-row'
                  key={key}
                  align='baseline'
                >
                  <Form.Item
                    {...restField}
                    name={[name, 'source']}
                    label='Source'
                    rules={[{ required: true, message: 'Select source language' }]}
                    style={{ minWidth: 200, flex: 1 }}
                  >
                    <Select
                      placeholder='Source language'
                      showSearch
                      optionFilterProp='children'
                    >
                      {SUPPORTED_LANGUAGES.map(lang => (
                        <Select.Option key={`source-${key}-${lang}`} value={lang}>
                          {lang}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, 'target']}
                    label='Target'
                    rules={[{ required: true, message: 'Select target language' }]}
                    style={{ minWidth: 200, flex: 1 }}
                  >
                    <Select
                      placeholder='Target language'
                      showSearch
                      optionFilterProp='children'
                    >
                      {SUPPORTED_LANGUAGES.map(lang => (
                        <Select.Option key={`target-${key}-${lang}`} value={lang}>
                          {lang}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, 'tier']}
                    label='Tier'
                    rules={[{ required: true, message: 'Select a tier' }]}
                    style={{ minWidth: 160, flex: 0 }}
                  >
                    <Select placeholder='Choose tier'>
                      <Select.Option value='Best'>Best</Select.Option>
                      <Select.Option value='Better'>Better</Select.Option>
                      <Select.Option value='Good'>Good</Select.Option>
                    </Select>
                  </Form.Item>

                  <Button
                    type='text'
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => remove(name)}
                  />
                </Space>
              ))}
              <Button
                type='dashed'
                onClick={() => add({ tier: 'Best' })}
                icon={<PlusOutlined />}
                block
              >
                Add language pair override
              </Button>
            </>
          )}
        </Form.List>
      </Card>

      <div className='ai-model-form-block'>
        <Divider orientation="left">Language Restrictions</Divider>
        <Form.Item name='disabledLanguages' label='Disable this model in languages'>
          <Select mode='multiple' allowClear placeholder='Select languages to disable'>
            {SUPPORTED_LANGUAGES.map(lang => (
              <Select.Option key={lang} value={lang}>{lang}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Divider orientation="left">Settings</Divider>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name='isActive' label='Active' valuePropName='checked'>
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='isFree' label='Free Model' valuePropName='checked'>
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='allowsNSFW' label='Allows NSFW' valuePropName='checked'>
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='testingOnly' label='Testing Only' valuePropName='checked'>
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name='internalOnly'
              label='Internal Only'
              valuePropName='checked'
              tooltip='Hidden from customer catalogs and rejected by production model validation'
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name='launchReady' label='Launch Ready' valuePropName='checked'>
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name='tags' label='Tags'>
          <Select mode='tags' placeholder="Add tags">
            <Select.Option value='Legacy'>Legacy</Select.Option>
            <Select.Option value='Beta'>Beta</Select.Option>
            <Select.Option value='Premium'>Premium</Select.Option>
          </Select>
        </Form.Item>
      </div>

      <Form.Item className='ai-model-form-footer'>
        <Space>
          <Button type='primary' htmlType='submit'>
            {isAdd ? 'Create Model' : 'Save Changes'}
          </Button>
          <Button onClick={handleModalClose}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )

  return (
    <div className='ai-model-manager'>
      <div className='ai-model-manager-header'>
        <div>
          <span className='ai-model-manager-kicker'>Admin workflow</span>
          <h3>AI Model Manager</h3>
        </div>
        <Space className='ai-model-manager-header__actions' wrap>
          <Tag>{filteredModels.length} shown</Tag>
          <Tag>{models.length} loaded</Tag>
        </Space>
      </div>

      {inlineError && (
        <Alert
          className='ai-model-manager-inline-alert'
          type='error'
          showIcon
          message='AI model manager needs attention'
          description={inlineError}
        />
      )}

      <Card
        className='ai-model-manager-section'
        title='AI Testing Tier Recommendations'
        extra={
          <Space>
            <Button
              onClick={fetchTierRecommendations}
              loading={recommendationLoading}
            >
              Generate Recommendations
            </Button>
            <Button
              type='primary'
              onClick={handleApplyRecommendations}
              loading={recommendationApplying}
              disabled={!selectedRecommendationModelIds.length}
            >
              Apply Selected ({selectedRecommendationModelIds.length})
            </Button>
          </Space>
        }
      >
        <Space direction='vertical' style={{ width: '100%' }} size='middle'>
          <div className='ai-model-manager-muted'>
            Uses non-proofread tests only and model-only performance across active, non-legacy models.
            Testing models are excluded by default.
          </div>
          <Checkbox
            checked={includeTestingModels}
            onChange={(e) => setIncludeTestingModels(e.target.checked)}
          >
            Include testing models
          </Checkbox>
          <Row gutter={16}>
            <Col span={8}>
              <div className='ai-model-manager-field-label'>Language</div>
              <Select
                value={recommendationLanguage}
                onChange={setRecommendationLanguage}
                style={{ width: '100%' }}
                showSearch
                optionFilterProp='children'
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <Select.Option key={`recommend-lang-${lang}`} value={lang}>
                    {lang}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <div className='ai-model-manager-field-label'>From Date (optional)</div>
              <Input
                type='date'
                value={recommendationDateFrom}
                onChange={(e) => setRecommendationDateFrom(e.target.value)}
              />
            </Col>
            <Col span={8}>
              <div className='ai-model-manager-field-label'>To Date (optional)</div>
              <Input
                type='date'
                value={recommendationDateTo}
                onChange={(e) => setRecommendationDateTo(e.target.value)}
              />
            </Col>
          </Row>
          {recommendationResult && (
            <Space size={[0, 8]} wrap>
              <Tag>Rows scanned: {recommendationResult.summary.rows_scanned}</Tag>
              <Tag>No-proofread rows: {recommendationResult.summary.rows_after_proofreader_filter}</Tag>
              <Tag>Eligible models: {recommendationResult.summary.eligible_models_total}</Tag>
              <Tag>Meeting min tests: {recommendationResult.summary.models_meeting_min_tests}</Tag>
              <Tag>Proposals: {recommendationResult.summary.models_with_proposals}</Tag>
            </Space>
          )}
          <div className='ai-model-manager-table-shell'>
            <Table<TierRecommendationItem>
              dataSource={recommendationItems}
              columns={recommendationColumns}
              rowKey='model_id'
              loading={recommendationLoading}
              rowSelection={recommendationRowSelection}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          </div>
        </Space>
      </Card>

      <div className='ai-model-manager-toolbar'>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
          >
            Add New Model
          </Button>
          <Button onClick={() => fetchModels(true, showInactive)}>
            Refresh Data
          </Button>
        </Space>
        <Space className='ai-model-manager-toolbar__meta' align="center" wrap>
          <Checkbox
            checked={showInactive}
            onChange={(e) => handleShowInactiveChange(e.target.checked)}
          >
            Show Inactive Models
          </Checkbox>
          <span>
            Showing {filteredModels.length} of {models.length} models
          </span>
        </Space>
      </div>

      <div className='ai-model-manager-table-shell'>
        <Table<AIModel>
          dataSource={filteredModels}
          columns={columns}
          rowKey='id'
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{ pageSize: 20 }}
        />
      </div>

      <Modal
        className='ai-model-manager-modal'
        title='Edit AI Model'
        open={editModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {renderModelForm(form, false)}
      </Modal>

      <Modal
        className='ai-model-manager-modal'
        title='Add New AI Model'
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {renderModelForm(addForm, true)}
      </Modal>
    </div>
  )
}

export default AIModelManager
